

export const AUTH_USER_STORAGE_KEY = "user";
export const AUTH_STATE_INVALIDATED_EVENT = "auth:state-invalidated";

const AUTH_USER_BLOCKED_KEYS = new Set([
  "password",
  "googleDriveOauthTokensJson",
  "googleDriveServiceAccountJson",
]);

const AUTH_USER_STORAGE_ONLY_BLOCKED_KEYS = new Set([
  "googleDriveRootFolderId",
  "settingsJson",
]);

type AuthInvalidationReason = "logout" | "unauthorized";

const sanitizeAuthPayload = (
  value: unknown,
  blockedKeys: Set<string>
): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeAuthPayload(item, blockedKeys));
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  const sanitizedEntries = Object.entries(value as Record<string, unknown>)
    .filter(([key]) => !blockedKeys.has(key))
    .map(([key, entryValue]) => [key, sanitizeAuthPayload(entryValue, blockedKeys)]);

  return Object.fromEntries(sanitizedEntries);
};

export const sanitizeAuthUser = <T = unknown>(user: T): T =>
  sanitizeAuthPayload(user, AUTH_USER_BLOCKED_KEYS) as T;

export const sanitizeStoredAuthUser = <T = unknown>(user: T): T =>
  sanitizeAuthPayload(
    user,
    new Set(
      Array.from(AUTH_USER_BLOCKED_KEYS).concat(
        Array.from(AUTH_USER_STORAGE_ONLY_BLOCKED_KEYS)
      )
    )
  ) as T;

export const clearStoredAuthUser = (): void => {
  localStorage.removeItem(AUTH_USER_STORAGE_KEY);
};

export const getStoredAuthUser = <T = unknown>(): T | null => {
  const rawUser = localStorage.getItem(AUTH_USER_STORAGE_KEY);
  if (!rawUser) {
    return null;
  }

  try {
    const parsedUser = JSON.parse(rawUser) as T;
    const sanitizedUser = sanitizeStoredAuthUser(parsedUser);

    if (JSON.stringify(sanitizedUser) !== rawUser) {
      localStorage.setItem(
        AUTH_USER_STORAGE_KEY,
        JSON.stringify(sanitizedUser)
      );
    }

    return sanitizedUser;
  } catch (error) {
    clearStoredAuthUser();
    return null;
  }
};

export const storeAuthUser = (user: unknown): void => {
  localStorage.setItem(
    AUTH_USER_STORAGE_KEY,
    JSON.stringify(sanitizeStoredAuthUser(user))
  );
};

export const getAuthErrorStatus = (error: unknown): number | null => {
  if (!error || typeof error !== "object") {
    return null;
  }

  const status = (error as { response?: { status?: unknown } }).response?.status;
  return typeof status === "number" ? status : null;
};

export const isUnauthorizedAuthError = (error: unknown): boolean =>
  getAuthErrorStatus(error) === 401;

export const notifyAuthStateInvalidated = (
  reason: AuthInvalidationReason
): void => {
  window.dispatchEvent(
    new CustomEvent(AUTH_STATE_INVALIDATED_EVENT, {
      detail: { reason },
    })
  );
};
